import { useEffect, useState } from "react";
import { PagedResults } from "../../types/PagedResults";
import { Article } from "../../types/Article";
import { useNavigate, useSearchParams } from "react-router-dom";
import useArticlesService from "../../services/ArticlesService";
import PageTitle from "../../components/PageTitle";
import Ghost from "../../components/Ghost";
import PositiveButton from "../../components/PositiveButton";
import ArrowForwardTwoToneIcon from "@mui/icons-material/ArrowForwardTwoTone";
import AddIcon from "@mui/icons-material/Add";
import CheckIcon from "@mui/icons-material/Check"
import XIcon from '@mui/icons-material/Close';

export default function AdminArticlesListPage() {
  const [isSearching, setIsSearching] = useState(false);
  const [results, setResults] = useState<PagedResults<Article> | null>(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    searchValue: searchParams.get("searchValue") || "",
    page: parseInt(searchParams.get("page") || "1"),
    includeArchive: searchParams.get("includeArchive") === "true",
  });

  const { searchArticles } = useArticlesService();
  const debounceDelay = 500;

  const search = () => {
    setIsSearching(true);

    setSearchParams({
      searchValue: formData.searchValue,
      page: formData.page.toString(),
      includeArchive: formData.includeArchive.toString(),
    });

    searchArticles(
      formData.searchValue,
      formData.page,
      formData.includeArchive,
      10
    )
      .then((searchResults) => {
        setResults(searchResults);
      })
      .catch((error) => {
        console.error("Error searching articles:", error);
      })
      .finally(() => {
        setIsSearching(false);
      });
  };

  const handleInputChange = (e: any) => {
    const { name, value } = e.target;

    setFormData({
      ...formData,
      [name]: value,
      page: 1, // Reset to the first page when searching
    });
  };

  const handleIncludeArchiveChange = (e: any) => {
    const { checked } = e.target;
    setFormData({
      ...formData,
      includeArchive: checked,
      page: 1, // Reset to the first page when searching
    });
  };

  const handlePageChange = (newPage: number) => {
    setFormData((prev) => ({
      ...prev,
      page: newPage,
    }));
  };

  useEffect(() => {
    const handler = setTimeout(() => {
      search();
    }, debounceDelay);

    return () => {
      clearTimeout(handler);
    };
  }, [formData.searchValue, formData.page, formData.includeArchive]);

  useEffect(() => {
    search();
  }, []);

  return (
    <div className="flex flex-col gap-4">
      <section role="heading" className="flex flex-row">
        <PageTitle className="mr-4">Articles</PageTitle>
        <PositiveButton onClick={() => navigate("/admin/articles/new")}>
          <AddIcon /> Add new title
        </PositiveButton>
        <div className="flex flex-row gap-2 px-4 items-center">
          <input
            disabled={isSearching}
            type="checkbox"
            name="includeArchive"
            id="includeArchive"
            value={formData.includeArchive.toString()}
            onChange={handleIncludeArchiveChange}
            className="w-6 h-6"
          />
          <label htmlFor="includeArchive">Include archived</label>
        </div>
        <div
          role="searchbox"
          className="flex flex-row gap-2  px-4 items-center xl:col-span-2 flex-grow"
        >
          <label
            htmlFor="search"
            className="hidden xl:w-auto xl:block leading-6"
          >
            Filter:
          </label>
          <div className="mt-2 w-full">
            <input
              type="text"
              name="searchValue"
              placeholder="Search term"
              value={formData.searchValue}
              onChange={handleInputChange}
              className="w-full px-3 py-2 border rounded text-black"
            />
          </div>
        </div>
      </section>
      <section role="content">
        <table className="table-auto w-full card border-spacing-2">
          <thead>
            <tr className="">
              <th className="font-normal text-left p-4">Image</th>
              <th className="font-normal text-left p-4">Title</th>
              <th className="font-normal text-left p-4">Priority</th>
              {formData.includeArchive && <th className="font-normal text-left p-4">Archived</th>}
              <th className="font-normal text-left p-4"></th>
            </tr>
          </thead>
          <tbody>
            {isSearching &&
              Array.from({ length: 3 }, (_, i) => (
                <tr className="border-t border-cyan-900" key={i}>
                  <td className="text-left p-4">
                    <Ghost additionalClasses="p-4 rounded" />
                  </td>
                  <td className="text-left p-4">
                    <Ghost additionalClasses="p-4 rounded" />
                  </td>
                  <td className="text-left p-4">
                    <Ghost additionalClasses="p-4 rounded" />
                  </td>
                  {formData.includeArchive && <td className="text-left p-4">
                    <Ghost additionalClasses="p-4 rounded" />
                  </td>}
                </tr>
              ))}

            {!isSearching && results && (
              <>
                {!results.items || results.items.length === 0 ? (
                  <tr className="border-t border-cyan-900">
                    <td className="text-left p-4" colSpan={formData.includeArchive ? 6 : 5}>
                      No results found.
                    </td>
                  </tr>
                ) : (
                  results.items.map((article) => (
                    <tr
                      className="border-t border-cyan-900 hover:bg-cyan-300/25 cursor-pointer"
                      key={article.id}
                      onClick={() => navigate(`/admin/articles/${article.id}/`)}
                      title={`View article ${article.title}`}
                    >
                      <td className="text-left p-4"><img src={article.publicImageUrl} alt={article.title} className="w-32 h-18 object-cover rounded" /></td>
                      <td className="text-left p-4">{article.title}</td>
                      <td className="text-left p-4">{article.priority}</td>
                      {formData.includeArchive && <td className="text-left p-4">{article.isArchived && <CheckIcon />}{!article.isArchived && <XIcon />}</td>}
                      <td className="p-4 w-auto text-right">
                        <ArrowForwardTwoToneIcon />
                      </td>
                    </tr>
                  ))
                )}
              </>
            )}
          </tbody>
        </table>
      </section>

      {/* Pagination Section */}
      {results && results.totalPages > 1 && (
        <section className="flex justify-between items-center mt-4">
          <PositiveButton
            onClick={() => handlePageChange(formData.page - 1)}
            disabled={formData.page === 1}
          >
            Previous
          </PositiveButton>
          <span>
            Page {formData.page} of {results.totalPages}
          </span>
          <PositiveButton
            onClick={() => handlePageChange(formData.page + 1)}
            disabled={formData.page === results.totalPages}
          >
            Next
          </PositiveButton>
        </section>
      )}
    </div>
  );
}
